import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import remarkBreaks from 'remark-breaks';
import { createPost, CreatePostData, updatePost, UpdatePostData, getPost, getPosts, generatePostContent } from '../../shared/api/posts';
import { uploadImageFile, getMediaList, type MediaItem } from '../../shared/api/media';
import { getCategories } from '../../shared/api/categories';
import { getTags, type Tag } from '../../shared/api/tags';
import { useAuth } from '../../shared/auth/AuthContext';
import { getAdminLoginPath, getAdminPostsPath, getUserPostsPath } from '../../shared/config/env';
import { getSiteUrl } from '../../shared/utils/metaTags';
import { generatePostContentPrompt } from '../../shared/prompts/generatePostContent';
import { Post } from '../../shared/types/post';
import ArticleLinkCard from '../../user/components/ArticleLinkCard';
import AdminSidebar from '../components/AdminSidebar';
import '../../shared/styles/markdown-content.css';
import './PostCreate.css';

// 共通の文字列定数
const LABELS = {
  TITLE: 'タイトル',
  SLUG: 'スラッグ',
  CONTENT: '本文',
  EXCERPT: '抜粋',
  CATEGORY: 'カテゴリー',
  STATUS: 'ステータス',
  TAGS: 'タグ',
  THUMBNAIL_URL: 'サムネイルURL',
  DRAFT: '下書き',
  PUBLISHED: '公開',
} as const;

const PLACEHOLDERS = {
  TITLE: '記事のタイトルを入力',
  SLUG: 'url-friendly-slug',
  EXCERPT: '記事の抜粋を入力（オプション）',
  CATEGORY: 'カテゴリー名',
  TAGS: 'タグ1, タグ2, タグ3',
  THUMBNAIL_URL: 'https://example.com/image.jpg',
} as const;

const BUTTONS = {
  GENERATE_HAIKU: 'Haikuで生成',
  GENERATING: '生成中...',
  OPEN_FILE: 'ファイルを開く',
  RELOAD_FILE: '更新',
  DOWNLOAD_FILE: 'ファイルとしてダウンロード',
  SELECT_IMAGE: '画像を選択',
  SELECT_UPLOADED_IMAGE: 'アップロード済み画像を選択',
  SELECT_FILE: 'ファイル選択',
  SELECT_UPLOADED_IMAGE_FOR_THUMBNAIL: 'アップロード済み画像を選択',
} as const;

const MESSAGES = {
  ENTER_CONTENT: '本文を入力してください',
  GENERATION_FAILED: 'コンテンツの生成に失敗しました',
  FILE_NOT_OPEN: 'ファイルが開かれていません',
  RELOAD_FAILED: 'ファイルの再読み込みに失敗しました',
  NO_CONTENT_TO_DOWNLOAD: 'ダウンロードする内容がありません',
  FILE_LOAD_FAILED: 'ファイルの読み込みに失敗しました',
  FILE_SYSTEM_API_NOT_SUPPORTED: 'このブラウザではFile System Access APIがサポートされていません。ChromeまたはEdgeをご使用ください。',
  SLUG_DESCRIPTION_EDIT: 'URLで使用されるスラッグ（変更すると既存のリンクが壊れる可能性があります）',
  SLUG_DESCRIPTION_NEW: 'URLで使用されるスラッグ（例: my-first-post）',
  EXCERPT_DESCRIPTION: '記事の概要や抜粋を入力します',
  TAGS_DESCRIPTION: 'カンマ区切りで複数のタグを入力できます',
  THUMBNAIL_DESCRIPTION: '記事のサムネイル画像のURL（直接入力またはアップロード）',
  LOADED_FILE: '読み込み済み',
  SELECTED_IMAGE: '選択中の画像',
  CURRENT_THUMBNAIL: '現在のサムネイル',
  FAILED_TO_LOAD_IMAGE: '画像を読み込めませんでした',
  MERMAID_RENDER_FAILED: 'Mermaid図表のレンダリングに失敗しました',
} as const;

/**
 * PostIDベースの記事リンクカードを表示するラッパーコンポーネント
 * 非同期で記事を取得し、カードを表示する
 */
function ArticleLinkCardWrapperByPostId({ postId, lang }: { postId: string; lang: 'ja' | 'en' }) {
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const postsPath = getUserPostsPath();
  const siteUrl = getSiteUrl();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(false);
        // PostIDで記事を取得（公開済み記事のみ）
        const response = await getPosts({ 
          post_ids: [postId], 
          lang,
          status: 'published',
          skipAuth: true 
        });
        
        if (response.posts && response.posts.length > 0) {
          const data = response.posts[0];
          // 公開済み記事のみカードを表示
          if (data.status === 'published') {
            setPost(data);
          } else {
            setError(true);
          }
        } else {
          setError(true);
        }
      } catch (err) {
        // エラー時は通常のリンクにフォールバック
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId, lang]);

  // ローディング中またはエラー時は何も表示しない（空のリンクテキストなので）
  if (loading || error || !post) {
    return null;
  }

  // 記事が取得できた場合はカードを表示
  // 現在の言語に合わせたURLを生成
  const url = `${siteUrl}${lang === 'ja' ? '/ja' : '/en'}${postsPath}/${post.slug}`;
  return <ArticleLinkCard post={post} lang={lang} url={url} />;
}

function PostCreate() {
  const { slug: urlSlug } = useParams<{ slug?: string }>();
  const isEditMode = urlSlug !== undefined;
  
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [tagsEn, setTagsEn] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedFileName, setLoadedFileName] = useState<string | null>(null);
  const [fileHandle, setFileHandle] = useState<FileSystemFileHandle | null>(null);
  const [postId, setPostId] = useState<string | null>(null);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadModalType, setUploadModalType] = useState<'thumbnail' | 'image' | null>(null);
  const [uploadModalFile, setUploadModalFile] = useState<File | null>(null);
  const [uploadModalFilename, setUploadModalFilename] = useState<string>('');
  const [uploadModalPreview, setUploadModalPreview] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<boolean>(false);
  const [thumbnailPreviewError, setThumbnailPreviewError] = useState<boolean>(false);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [mediaLibraryMode, setMediaLibraryMode] = useState<'thumbnail' | 'image' | null>(null);
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [mediaPage, setMediaPage] = useState(1);
  const [mediaHasNext, setMediaHasNext] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState<'ja' | 'en'>('ja');
  // 英語版の状態管理
  const [titleEn, setTitleEn] = useState('');
  const [slugEn, setSlugEn] = useState('');
  const [contentEn, setContentEn] = useState('');
  const [excerptEn, setExcerptEn] = useState('');
  const [loadedFileNameEn, setLoadedFileNameEn] = useState<string | null>(null);
  const [fileHandleEn, setFileHandleEn] = useState<FileSystemFileHandle | null>(null);
  const [isGeneratingEn, setIsGeneratingEn] = useState(false);
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [existingTags, setExistingTags] = useState<Tag[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorContainerRefEn = useRef<HTMLDivElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const imageInputRefEn = useRef<HTMLInputElement>(null);
  const [editorHeight, setEditorHeight] = useState(500);
  
  // リンクテキストを抽出する関数
  const extractTextFromChildren = (children: any): string => {
    if (typeof children === 'string') {
      return children;
    }
    if (typeof children === 'number') {
      return String(children);
    }
    if (Array.isArray(children)) {
      return children.map(extractTextFromChildren).join('');
    }
    if (children && typeof children === 'object' && 'props' in children && children.props) {
      return extractTextFromChildren(children.props.children);
    }
    return '';
  };
  
  const { logout } = useAuth();
  const navigate = useNavigate();
  const adminLoginPath = getAdminLoginPath();
  const adminPostsPath = getAdminPostsPath();

  // カテゴリー一覧を取得
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const response = await getCategories();
        // 日本語名でソート（orderフィールドがあればそれを使用、なければnameでソート）
        const sortedCategories = response.categories
          .filter(cat => cat.isVisible) // 表示されているカテゴリーのみ
          .sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            if (a.order !== undefined) return -1;
            if (b.order !== undefined) return 1;
            return a.name.localeCompare(b.name, 'ja');
          })
          .map(cat => ({ id: cat.id, name: cat.name }));
        setCategories(sortedCategories);
      } catch (err) {
        if (err instanceof Error && (err as any).isAuthError) {
          logout();
          navigate(adminLoginPath, { replace: true });
          return;
        }
        // カテゴリー取得エラーは非致命的なので、エラーメッセージは表示しない
        console.error('カテゴリー一覧の取得に失敗しました:', err);
      } finally {
        setIsLoadingCategories(false);
      }
    };
    
    fetchCategories();
  }, [logout, navigate, adminLoginPath]);

  // 既存タグ一覧を取得
  useEffect(() => {
    const fetchTags = async () => {
      try {
        setIsLoadingTags(true);
        const response = await getTags();
        setExistingTags(response.tags);
      } catch (err) {
        if (err instanceof Error && (err as any).isAuthError) {
          logout();
          navigate(adminLoginPath, { replace: true });
          return;
        }
        // タグ取得失敗はエラーを表示しない（オプショナルな機能のため）
        console.warn('タグ一覧の取得に失敗しました:', err);
      } finally {
        setIsLoadingTags(false);
      }
    };
    fetchTags();
  }, [logout, navigate, adminLoginPath]);

  // 既存タグ一覧が読み込まれた後、記事のタグ名と既存タグをマッチングしてIDを設定
  useEffect(() => {
    if (tags && existingTags.length > 0 && selectedTagIds.length === 0) {
      const tagNames = tags.split(',').map((t) => t.trim()).filter((t) => t.length > 0);
      const matchedIds: string[] = [];
      const matchedNames: string[] = [];
      const matchedNamesEn: string[] = [];
      
      tagNames.forEach((tagName) => {
        const matchedTag = existingTags.find((tag) => tag.name === tagName);
        if (matchedTag) {
          matchedIds.push(matchedTag.id);
          matchedNames.push(matchedTag.name);
          if (matchedTag.nameEn) {
            matchedNamesEn.push(matchedTag.nameEn);
          }
        }
      });
      
      if (matchedIds.length > 0) {
        setSelectedTagIds(matchedIds);
        setTags(matchedNames.join(', '));
        if (matchedNamesEn.length > 0) {
          setTagsEn(matchedNamesEn.join(', '));
        }
      }
    }
  }, [tags, existingTags, selectedTagIds.length]);

  // 編集モード時、既存記事データを読み込む
  useEffect(() => {
    if (isEditMode && urlSlug) {
      const fetchPost = async () => {
        try {
          setIsLoading(true);
          setError(null);
          const post = await getPost(urlSlug);
          setPostId(post.post_id);
          setTitle(post.title);
          setSlug(post.slug);
          setContent(post.content);
          setExcerpt(post.excerpt || '');
          // カテゴリーは後でカテゴリー一覧が読み込まれたときにマッチングする
          setCategory(post.category || '');
          // タグはIDで送信されるため、記事読み込み時はタグ名を表示用に設定
          // 既存タグ一覧が読み込まれた後に、IDとマッチングする
          setTags(post.tags?.join(', ') || '');
          // 英語版のタグはAPIから返ってこない場合があるため、空文字列をデフォルトとする
          // 将来的にAPIがtagsEnを返すようになったら、それを使用する
          setTagsEn('');
          // タグIDは既存タグ一覧が読み込まれた後に設定する
          setSelectedTagIds([]);
          setThumbnailUrl(post.thumbnail_url || '');
          setStatus(post.status === 'archived' ? 'draft' : post.status);
          setTitleEn(post.titleEn || '');
          setContentEn(post.contentEn || '');
          setExcerptEn(post.excerptEn || '');
        } catch (err) {
          if (err instanceof Error && (err as any).isAuthError) {
            logout();
            navigate(adminLoginPath, { replace: true });
            return;
          }
          if (err instanceof Error) {
            setError(err.message);
          } else {
            setError('記事の取得に失敗しました');
          }
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchPost();
    }
  }, [isEditMode, urlSlug, logout, navigate, adminLoginPath]);

  // カテゴリー一覧が読み込まれた後、既存のカテゴリー値を再マッチング
  useEffect(() => {
    if (category && categories.length > 0) {
      // 既にIDでマッチングされている場合は何もしない
      const categoryById = categories.find(cat => cat.id === category);
      if (categoryById) {
        return;
      }
      // IDでマッチングできない場合は、カテゴリー名でマッチングを試みる
      const categoryByName = categories.find(cat => cat.name === category);
      if (categoryByName) {
        setCategory(categoryByName.id);
      }
    }
  }, [categories, category]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  // 英語版のタイトル変更ハンドラー
  const handleTitleChangeEn = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleEn(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // バリデーション
    if (!title.trim()) {
      setError('タイトルは必須です');
      return;
    }
    if (!slug.trim()) {
      setError('スラッグは必須です');
      return;
    }
    if (!content.trim()) {
      setError('本文は必須です');
      return;
    }
    if (!category.trim()) {
      setError('カテゴリーは必須です');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        // 編集モード
        if (!postId) {
          setError('記事IDが取得できませんでした');
          setIsSubmitting(false);
          return;
        }
        
        const postData: UpdatePostData = {
          title: title.trim(),
          slug: slug.trim(),
          content: content.trim(),
          status,
          ...(excerpt.trim() && { excerpt: excerpt.trim() }),
          category: category.trim(),
          ...(selectedTagIds.length > 0 && { 
            tags: selectedTagIds
          }),
          ...(thumbnailUrl.trim() && { thumbnail_url: thumbnailUrl.trim() || null }),
          ...(titleEn.trim() && { titleEn: titleEn.trim() }),
          ...(contentEn.trim() && { contentEn: contentEn.trim() }),
          ...(excerptEn.trim() && { excerptEn: excerptEn.trim() }),
        };

        await updatePost(postId, postData);
        // 更新成功後、記事一覧にリダイレクト
        navigate(adminPostsPath);
      } else {
        // 作成モード
        const postData: CreatePostData = {
          title: title.trim(),
          slug: slug.trim(),
          content: content.trim(),
          status,
          ...(excerpt.trim() && { excerpt: excerpt.trim() }),
          category: category.trim(),
          ...(selectedTagIds.length > 0 && { 
            tags: selectedTagIds
          }),
          ...(thumbnailUrl.trim() && { thumbnail_url: thumbnailUrl.trim() || null }),
          ...(titleEn.trim() && { titleEn: titleEn.trim() }),
          ...(contentEn.trim() && { contentEn: contentEn.trim() }),
          ...(excerptEn.trim() && { excerptEn: excerptEn.trim() }),
        };

        await createPost(postData);
        // 作成成功後、記事一覧にリダイレクト
        navigate(adminPostsPath);
      }
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('記事の作成に失敗しました');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(adminPostsPath);
  };

  /**
   * サムネイル画像ファイル選択ダイアログを開く
   */
  const handleThumbnailFileSelect = () => {
    if (!thumbnailInputRef.current) {
      return;
    }
    thumbnailInputRef.current.click();
  };

  /**
   * ファイル名から拡張子を取得する
   */
  const getFileExtension = (fileName: string): string => {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot >= 0 ? fileName.substring(lastDot) : '';
  };

  /**
   * ファイル名のバリデーション
   */
  const validateFilename = (name: string): string | null => {
    if (!name || name.trim() === '') {
      return 'ファイル名を入力してください';
    }
    
    // 無効な文字をチェック
    const invalidChars = /[<>:"/\\|?*\x00-\x1f]/;
    if (invalidChars.test(name)) {
      return 'ファイル名に無効な文字が含まれています（< > : " / \\ | ? * および制御文字は使用できません）';
    }
    
    // 長さチェック（255文字以下）
    if (name.length > 255) {
      return 'ファイル名は255文字以下である必要があります';
    }
    
    return null;
  };

  /**
   * ファイルからプレビュー画像のURLを生成する
   */
  const createPreviewUrl = (file: File): string => {
    return URL.createObjectURL(file);
  };

  /**
   * プレビューURLをクリーンアップする
   */
  const cleanupPreviewUrl = (url: string | null) => {
    if (url) {
      URL.revokeObjectURL(url);
    }
  };

  /**
   * 画像ファイル選択時の共通処理
   */
  const handleImageFileSelect = (file: File, type: 'thumbnail' | 'image') => {
    // 画像ファイルかチェック
    if (!file.type.startsWith('image/')) {
      // モーダルが開いている場合はエラーを表示、開いていない場合はエラーを表示して終了
      if (isUploadModalOpen) {
        setError('画像ファイルを選択してください');
      } else {
        setError('画像ファイルを選択してください');
        return;
      }
      return;
    }

    // 既存のプレビューURLをクリーンアップ
    if (uploadModalPreview) {
      cleanupPreviewUrl(uploadModalPreview);
    }

    setUploadModalType(type);
    setUploadModalFile(file);
    setUploadModalFilename(file.name);
    setPreviewError(false);
    // 画像ファイルの形式をチェック（ブラウザで表示可能な形式か）
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (supportedTypes.includes(file.type)) {
      setUploadModalPreview(createPreviewUrl(file));
    } else {
      setUploadModalPreview(null);
      setPreviewError(true);
    }
    setIsUploadModalOpen(true);
    setError(null);
  };

  /**
   * サムネイル画像ファイル選択時の処理
   */
  const handleThumbnailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    handleImageFileSelect(file, 'thumbnail');
  };

  /**
   * モーダルから画像をアップロードする（共通処理）
   */
  const handleUpload = async () => {
    if (!uploadModalFile || !uploadModalType) {
      return;
    }

    // ファイル名のバリデーション
    const filenameError = validateFilename(uploadModalFilename);
    if (filenameError) {
      setError(filenameError);
      return;
    }

    // アップロード状態を設定
    if (uploadModalType === 'thumbnail') {
      setIsUploadingThumbnail(true);
    } else {
      setIsUploadingImage(true);
    }
    setError(null);

    try {
      // ファイル名に拡張子が含まれていない場合、元のファイル名から拡張子を取得
      const originalExtension = getFileExtension(uploadModalFile.name);
      const finalFilename = uploadModalFilename.includes('.') 
        ? uploadModalFilename 
        : uploadModalFilename + originalExtension;

      const imageUrl = await uploadImageFile(uploadModalFile, finalFilename);
      
      // タイプに応じて処理を分岐
      if (uploadModalType === 'thumbnail') {
        setThumbnailUrl(imageUrl);
        setThumbnailPreviewError(false);
      } else {
        // Markdown形式で画像を挿入
        const imageMarkdown = `![${finalFilename}](${imageUrl})`;
        
        // アクティブなタブに応じて適切なコンテンツに挿入
        if (activeTab === 'en') {
          // 英語版のコンテンツに挿入
          const editorElement = editorContainerRefEn.current?.querySelector('textarea');
          if (editorElement) {
            const start = (editorElement as HTMLTextAreaElement).selectionStart;
            const end = (editorElement as HTMLTextAreaElement).selectionEnd;
            const beforeCursor = contentEn.substring(0, start);
            const afterCursor = contentEn.substring(end);
            const newContent = beforeCursor + imageMarkdown + (afterCursor ? '\n\n' + afterCursor : '');
            setContentEn(newContent);
            
            // カーソル位置を画像の後に設定
            setTimeout(() => {
              const newCursorPos = start + imageMarkdown.length + 2;
              (editorElement as HTMLTextAreaElement).setSelectionRange(newCursorPos, newCursorPos);
              editorElement.focus();
            }, 0);
          } else {
            // カーソル位置が取得できない場合は末尾に挿入
            setContentEn(contentEn + (contentEn ? '\n\n' : '') + imageMarkdown);
          }
        } else {
          // 日本語版のコンテンツに挿入
          const editorElement = editorContainerRef.current?.querySelector('textarea');
          if (editorElement) {
            const start = (editorElement as HTMLTextAreaElement).selectionStart;
            const end = (editorElement as HTMLTextAreaElement).selectionEnd;
            const beforeCursor = content.substring(0, start);
            const afterCursor = content.substring(end);
            const newContent = beforeCursor + imageMarkdown + (afterCursor ? '\n\n' + afterCursor : '');
            setContent(newContent);
            
            // カーソル位置を画像の後に設定
            setTimeout(() => {
              const newCursorPos = start + imageMarkdown.length + 2;
              (editorElement as HTMLTextAreaElement).setSelectionRange(newCursorPos, newCursorPos);
              editorElement.focus();
            }, 0);
          } else {
            // カーソル位置が取得できない場合は末尾に挿入
            setContent(content + (content ? '\n\n' : '') + imageMarkdown);
          }
        }
      }
      
      // アップロード成功後、モーダルを閉じる
      if (uploadModalPreview) {
        cleanupPreviewUrl(uploadModalPreview);
      }
      setIsUploadModalOpen(false);
      setUploadModalType(null);
      setUploadModalFile(null);
      setUploadModalFilename('');
      setUploadModalPreview(null);
      
      // ファイル入力のリセット
      if (uploadModalType === 'thumbnail' && thumbnailInputRef.current) {
        thumbnailInputRef.current.value = '';
      } else if (uploadModalType === 'image' && imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        setIsUploadModalOpen(false);
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('画像のアップロードに失敗しました');
      }
    } finally {
      if (uploadModalType === 'thumbnail') {
        setIsUploadingThumbnail(false);
      } else {
        setIsUploadingImage(false);
      }
    }
  };

  /**
   * Markdownに画像を挿入する
   */
  const handleInsertImage = async () => {
    if (!imageInputRef.current) {
      return;
    }

    imageInputRef.current.click();
  };

  /**
   * Markdownに画像を挿入する（英語版）
   */
  const handleInsertImageEn = async () => {
    if (!imageInputRefEn.current) {
      return;
    }

    imageInputRefEn.current.click();
  };

  /**
   * メディアライブラリを開く（本文用）
   */
  const handleOpenMediaLibrary = async () => {
    setMediaLibraryMode('image');
    setIsMediaLibraryOpen(true);
    setMediaError(null);
    setMediaPage(1);
    await fetchMediaList(1);
  };

  /**
   * メディアライブラリを開く（サムネイル用）
   */
  const handleOpenMediaLibraryForThumbnail = async () => {
    setMediaLibraryMode('thumbnail');
    setIsMediaLibraryOpen(true);
    setMediaError(null);
    setMediaPage(1);
    await fetchMediaList(1);
  };

  /**
   * メディア一覧を取得
   */
  const fetchMediaList = async (page: number) => {
    try {
      setIsLoadingMedia(true);
      setMediaError(null);
      const response = await getMediaList({
        page,
        per_page: 20,
        media_type: 'image',
      });
      setMediaList(response.media);
      setMediaHasNext(response.meta.has_next);
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        setIsMediaLibraryOpen(false);
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        setMediaError(err.message);
      } else {
        setMediaError('メディア一覧の取得に失敗しました');
      }
    } finally {
      setIsLoadingMedia(false);
    }
  };

  /**
   * 次のページを読み込む
   */
  const handleLoadMoreMedia = async () => {
    if (isLoadingMedia || !mediaHasNext) {
      return;
    }
    const nextPage = mediaPage + 1;
    setMediaPage(nextPage);
    await fetchMediaList(nextPage);
  };

  /**
   * アップロード済み画像を選択して挿入
   */
  const handleSelectMedia = (media: MediaItem) => {
    // CloudFront URLがない場合はエラー
    if (!media.cloudfront_url) {
      setMediaError(`この画像（${media.original_filename}）はCloudFront URLが設定されていないため、フロントエンドで表示できません。`);
      return;
    }
    
    const imageUrl = media.cloudfront_url;
    
    // モードに応じて処理を分岐
    if (mediaLibraryMode === 'thumbnail') {
      // サムネイルURLに設定
      setThumbnailUrl(imageUrl);
      setThumbnailPreviewError(false);
    } else {
      // Markdown形式で本文に挿入
      const imageMarkdown = `![${media.original_filename}](${imageUrl})`;
      
      // アクティブなタブに応じて適切なコンテンツに挿入
      if (activeTab === 'en') {
        // 英語版のコンテンツに挿入
        const editorElement = editorContainerRefEn.current?.querySelector('textarea');
        if (editorElement) {
          const start = (editorElement as HTMLTextAreaElement).selectionStart;
          const end = (editorElement as HTMLTextAreaElement).selectionEnd;
          const beforeCursor = contentEn.substring(0, start);
          const afterCursor = contentEn.substring(end);
          const newContent = beforeCursor + imageMarkdown + (afterCursor ? '\n\n' + afterCursor : '');
          setContentEn(newContent);
          
          // カーソル位置を画像の後に設定
          setTimeout(() => {
            const newCursorPos = start + imageMarkdown.length + 2;
            (editorElement as HTMLTextAreaElement).setSelectionRange(newCursorPos, newCursorPos);
            editorElement.focus();
          }, 0);
        } else {
          // カーソル位置が取得できない場合は末尾に挿入
          setContentEn(contentEn + (contentEn ? '\n\n' : '') + imageMarkdown);
        }
      } else {
        // 日本語版のコンテンツに挿入
        const editorElement = editorContainerRef.current?.querySelector('textarea');
        if (editorElement) {
          const start = (editorElement as HTMLTextAreaElement).selectionStart;
          const end = (editorElement as HTMLTextAreaElement).selectionEnd;
          const beforeCursor = content.substring(0, start);
          const afterCursor = content.substring(end);
          const newContent = beforeCursor + imageMarkdown + (afterCursor ? '\n\n' + afterCursor : '');
          setContent(newContent);
          
          // カーソル位置を画像の後に設定
          setTimeout(() => {
            const newCursorPos = start + imageMarkdown.length + 2;
            (editorElement as HTMLTextAreaElement).setSelectionRange(newCursorPos, newCursorPos);
            editorElement.focus();
          }, 0);
        } else {
          // カーソル位置が取得できない場合は末尾に挿入
          setContent(content + (content ? '\n\n' : '') + imageMarkdown);
        }
      }
    }
    
    // モーダルを閉じる
    setIsMediaLibraryOpen(false);
    setMediaLibraryMode(null);
  };

  /**
   * メディアライブラリを閉じる
   */
  const handleCloseMediaLibrary = () => {
    setIsMediaLibraryOpen(false);
    setMediaLibraryMode(null);
    setMediaList([]);
    setMediaError(null);
    setMediaPage(1);
    setMediaHasNext(false);
  };

  /**
   * タグ選択モーダルを開く
   */
  const handleOpenTagModal = () => {
    setIsTagModalOpen(true);
    setTagSearchQuery('');
  };

  /**
   * タグ選択モーダルを閉じる
   */
  const handleCloseTagModal = () => {
    setIsTagModalOpen(false);
    setTagSearchQuery('');
  };

  /**
   * タグの選択/解除を処理
   * 日本語版と英語版の両方のタグを更新
   * タグIDを保持し、表示用にタグ名も更新
   */
  const handleTagToggle = (tag: Tag) => {
    const tagNames = tags.split(',').map((t) => t.trim()).filter((t) => t.length > 0);
    const tagNamesEn = tagsEn.split(',').map((t) => t.trim()).filter((t) => t.length > 0);
    
    if (selectedTagIds.includes(tag.id)) {
      // タグを削除
      const newTagIds = selectedTagIds.filter((id) => id !== tag.id);
      setSelectedTagIds(newTagIds);
      
      const newTags = tagNames.filter((t) => t !== tag.name);
      setTags(newTags.join(', '));
      
      // 英語版のタグも削除
      if (tag.nameEn) {
        const newTagsEn = tagNamesEn.filter((t) => t !== tag.nameEn);
        setTagsEn(newTagsEn.join(', '));
      }
    } else {
      // タグを追加
      setSelectedTagIds([...selectedTagIds, tag.id]);
      
      if (!tagNames.includes(tag.name)) {
        tagNames.push(tag.name);
        setTags(tagNames.join(', '));
      }
      
      // 英語版のタグも追加
      if (tag.nameEn) {
        if (!tagNamesEn.includes(tag.nameEn)) {
          tagNamesEn.push(tag.nameEn);
        }
        setTagsEn(tagNamesEn.join(', '));
      }
    }
  };

  /**
   * 画像ファイル選択時の処理（Markdown用）
   */
  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    handleImageFileSelect(file, 'image');
  };

  /**
   * 画像ファイル選択時の処理（Markdown用・英語版）
   */
  const handleImageFileChangeEn = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    handleImageFileSelect(file, 'image');
  };

  /**
   * Haikuでタイトル、概要、Slugを生成する（日本語版）
   * 英語タイトルと英語概要も同時に生成して英語欄も埋める
   */
  const handleGenerateWithHaiku = async () => {
    if (!content.trim()) {
      setError(MESSAGES.ENTER_CONTENT);
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // プロンプトを生成
      const prompt = generatePostContentPrompt({
        content: content.trim(),
      });

      // タイトル、概要、Slug、英語タイトル、英語概要を生成
      const response = await generatePostContent({ 
        content: content.trim(),
        prompt: prompt,
      });

      // 日本語版の値を設定
      setTitle(response.title);
      setExcerpt(response.excerpt);
      setSlug(response.slug);

      // 英語版の値も設定
      setTitleEn(response.titleEn);
      setExcerptEn(response.excerptEn);
      // Slugは日本語版と同じものを使用
      setSlugEn(response.slug);
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(MESSAGES.GENERATION_FAILED);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Haikuでタイトル、概要、Slugを生成する（英語版）
   * 日本語版のコンテンツから生成し、英語タイトルと英語概要も同時に取得
   */
  const handleGenerateWithHaikuEn = async () => {
    if (!content.trim()) {
      setError(MESSAGES.ENTER_CONTENT);
      return;
    }

    setIsGeneratingEn(true);
    setError(null);

    try {
      // プロンプトを生成
      const prompt = generatePostContentPrompt({
        content: content.trim(),
      });

      // タイトル、概要、Slug、英語タイトル、英語概要を生成
      const response = await generatePostContent({ 
        content: content.trim(),
        prompt: prompt,
      });

      // 日本語版の値を設定（英語版タブで生成した場合でも日本語版に反映）
      setTitle(response.title);
      setExcerpt(response.excerpt);
      setSlug(response.slug);

      // 英語版の値を設定
      setTitleEn(response.titleEn);
      setExcerptEn(response.excerptEn);
      // Slugは日本語版と同じものを使用
      setSlugEn(response.slug);
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(MESSAGES.GENERATION_FAILED);
      }
    } finally {
      setIsGeneratingEn(false);
    }
  };

  /**
   * アップロードモーダルを閉じる
   */
  const handleCloseUploadModal = () => {
    // プレビューURLをクリーンアップ
    if (uploadModalPreview) {
      cleanupPreviewUrl(uploadModalPreview);
    }
    setIsUploadModalOpen(false);
    setUploadModalType(null);
    setUploadModalFile(null);
    setUploadModalFilename('');
    setUploadModalPreview(null);
    setPreviewError(false);
    setError(null);
    // ファイル入力のリセット
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  /**
   * モーダル内でファイルを再選択する
   */
  const handleReselectFile = () => {
    setError(null);
    // 既存のプレビューURLをクリーンアップ
    if (uploadModalPreview) {
      cleanupPreviewUrl(uploadModalPreview);
      setUploadModalPreview(null);
    }
    setPreviewError(false);
    if (uploadModalType === 'thumbnail' && thumbnailInputRef.current) {
      thumbnailInputRef.current.click();
    } else if (uploadModalType === 'image' && imageInputRef.current) {
      imageInputRef.current.click();
    }
  };

  // ファイルを読み込む（共通処理）
  const loadFileContent = async (file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        resolve(text);
      };
      reader.onerror = () => {
        reject(new Error('ファイルの読み込みに失敗しました'));
      };
      reader.readAsText(file, 'UTF-8');
    });
  };

  // File System Access APIでファイルを開く
  const handleOpenFile = async () => {
    // File System Access APIがサポートされているか確認
    if (!('showOpenFilePicker' in window)) {
      setError('このブラウザではFile System Access APIがサポートされていません。ChromeまたはEdgeをご使用ください。');
      return;
    }

    try {
      const [handle] = await (window as any).showOpenFilePicker({
        types: [
          {
            description: 'Markdown files',
            accept: {
              'text/markdown': ['.md', '.markdown'],
            },
          },
        ],
        multiple: false,
      });

      const file = await handle.getFile();
      const text = await loadFileContent(file);
      
      setContent(text);
      setLoadedFileName(file.name);
      setFileHandle(handle);
      setError(null);
    } catch (err: any) {
      // ユーザーがキャンセルした場合はエラーを表示しない
      if (err.name !== 'AbortError') {
        setError(err.message || 'ファイルの読み込みに失敗しました');
      }
    }
  };

  // File System Access APIでファイルを開く（英語版）
  const handleOpenFileEn = async () => {
    // File System Access APIがサポートされているか確認
    if (!('showOpenFilePicker' in window)) {
      setError(MESSAGES.FILE_SYSTEM_API_NOT_SUPPORTED);
      return;
    }

    try {
      const [handle] = await (window as any).showOpenFilePicker({
        types: [
          {
            description: 'Markdown files',
            accept: {
              'text/markdown': ['.md', '.markdown'],
            },
          },
        ],
        multiple: false,
      });

      const file = await handle.getFile();
      const text = await loadFileContent(file);
      
      setContentEn(text);
      setLoadedFileNameEn(file.name);
      setFileHandleEn(handle);
      setError(null);
    } catch (err: any) {
      // ユーザーがキャンセルした場合はエラーを表示しない
      if (err.name !== 'AbortError') {
        setError(err.message || MESSAGES.FILE_LOAD_FAILED);
      }
    }
  };

  // ファイルを手動で再読み込み
  const handleReloadFile = async () => {
    if (!fileHandle) {
      setError('ファイルが開かれていません');
      return;
    }

    try {
      const file = await fileHandle.getFile();
      const text = await loadFileContent(file);
      setContent(text);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ファイルの再読み込みに失敗しました');
    }
  };

  // ファイルを手動で再読み込み（英語版）
  const handleReloadFileEn = async () => {
    if (!fileHandleEn) {
      setError(MESSAGES.FILE_NOT_OPEN);
      return;
    }

    try {
      const file = await fileHandleEn.getFile();
      const text = await loadFileContent(file);
      setContentEn(text);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : MESSAGES.RELOAD_FAILED);
    }
  };

  // 現在の内容をMarkdownファイルとしてダウンロード
  const handleFileDownload = () => {
    if (!content.trim()) {
      setError('ダウンロードする内容がありません');
      return;
    }

    const fileName = slug || title || 'article';
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // 現在の内容をMarkdownファイルとしてダウンロード（英語版）
  const handleFileDownloadEn = () => {
    if (!contentEn.trim()) {
      setError(MESSAGES.NO_CONTENT_TO_DOWNLOAD);
      return;
    }

    const fileName = slugEn || titleEn || 'article';
    const blob = new Blob([contentEn], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // MDEditorのプレビューエリア内のMermaidコードブロックを処理
  useEffect(() => {
    if (!editorContainerRef.current) return;

    const processMermaidBlocks = () => {
      const previewArea = editorContainerRef.current?.querySelector('.w-md-editor-preview');
      if (!previewArea) {
        return;
      }

      // 既に処理済みのMermaidコンテナが存在する場合はスキップ
      const existingMermaidContainers = previewArea.querySelectorAll('.mermaid-container[data-processed="true"]');
      if (existingMermaidContainers.length > 0) {
        return;
      }

      // Mermaidコードブロックを検索
      const codeBlocks = previewArea.querySelectorAll('pre code.language-mermaid, pre code.lang-mermaid');
      
      
      codeBlocks.forEach((codeBlock) => {
        const preElement = codeBlock.parentElement;
        if (!preElement || preElement.hasAttribute('data-mermaid-processed')) return;

        const mermaidCode = codeBlock.textContent || '';
        if (!mermaidCode.trim()) return;

        // 既に処理済みの場合はスキップ
        preElement.setAttribute('data-mermaid-processed', 'true');

        // Mermaidコンテナを作成
        const mermaidContainer = document.createElement('div');
        mermaidContainer.className = 'mermaid-container';
        mermaidContainer.setAttribute('data-processed', 'true');
        
        // pre要素をMermaidコンテナで置き換え
        if (preElement.parentElement) {
          preElement.parentElement.replaceChild(mermaidContainer, preElement);
        }

        // Mermaidを直接レンダリング（共通化前の実装に戻す）
        const mermaidDiv = document.createElement('div');
        mermaidDiv.className = 'mermaid';
        mermaidContainer.appendChild(mermaidDiv);
        
        // mermaid.render()を直接呼び出してSVGをDOMに挿入（共通化前の実装）
        import('mermaid').then((mermaidModule) => {
          const mermaid = mermaidModule.default;
          mermaid.initialize({
            startOnLoad: false,
            theme: 'default',
            securityLevel: 'loose',
          });

          const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
          mermaid
            .render(id, mermaidCode)
            .then((result) => {
              mermaidDiv.innerHTML = result.svg;
            })
            .catch((err) => {
              console.error('Mermaid rendering error:', err);
              mermaidDiv.innerHTML = `<div class="mermaid-error"><p>Mermaid図表のレンダリングに失敗しました</p><pre>${err.message}</pre></div>`;
            });
        });
      });
    };

    // コンテンツが変更されたときに処理
    const observer = new MutationObserver(processMermaidBlocks);
    if (editorContainerRef.current) {
      observer.observe(editorContainerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    // 初回処理
    const timeoutId = setTimeout(processMermaidBlocks, 100);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [content]);

  // MDEditorのプレビューエリア内のMermaidコードブロックを処理（英語版）
  useEffect(() => {
    if (!editorContainerRefEn.current) return;

    const processMermaidBlocks = () => {
      const previewArea = editorContainerRefEn.current?.querySelector('.w-md-editor-preview');
      if (!previewArea) return;

      // 既に処理済みのMermaidコンテナが存在する場合はスキップ
      const existingMermaidContainers = previewArea.querySelectorAll('.mermaid-container[data-processed="true"]');
      if (existingMermaidContainers.length > 0) {
        return;
      }

      // Mermaidコードブロックを検索
      const codeBlocks = previewArea.querySelectorAll('pre code.language-mermaid, pre code.lang-mermaid');
      
      codeBlocks.forEach((codeBlock) => {
        const preElement = codeBlock.parentElement;
        if (!preElement || preElement.hasAttribute('data-mermaid-processed')) return;

        const mermaidCode = codeBlock.textContent || '';
        if (!mermaidCode.trim()) return;

        // 既に処理済みの場合はスキップ
        preElement.setAttribute('data-mermaid-processed', 'true');

        // Mermaidコンテナを作成
        const mermaidContainer = document.createElement('div');
        mermaidContainer.className = 'mermaid-container';
        mermaidContainer.setAttribute('data-processed', 'true');
        
        // pre要素をMermaidコンテナで置き換え
        if (preElement.parentElement) {
          preElement.parentElement.replaceChild(mermaidContainer, preElement);
        }

        // Mermaidを直接レンダリング（共通化前の実装に戻す）
        const mermaidDiv = document.createElement('div');
        mermaidDiv.className = 'mermaid';
        mermaidContainer.appendChild(mermaidDiv);
        
        // mermaid.render()を直接呼び出してSVGをDOMに挿入（共通化前の実装）
        import('mermaid').then((mermaidModule) => {
          const mermaid = mermaidModule.default;
          mermaid.initialize({
            startOnLoad: false,
            theme: 'default',
            securityLevel: 'loose',
          });

          const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
          mermaid
            .render(id, mermaidCode)
            .then((result) => {
              mermaidDiv.innerHTML = result.svg;
            })
            .catch((err) => {
              console.error('Mermaid rendering error:', err);
              mermaidDiv.innerHTML = `<div class="mermaid-error"><p>Mermaid図表のレンダリングに失敗しました</p><pre>${err.message}</pre></div>`;
            });
        });
      });
    };

    // コンテンツが変更されたときに処理
    const observer = new MutationObserver(processMermaidBlocks);
    if (editorContainerRefEn.current) {
      observer.observe(editorContainerRefEn.current, {
        childList: true,
        subtree: true,
      });
    }

    // 初回処理
    const timeoutId = setTimeout(processMermaidBlocks, 100);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [contentEn]);

  // エディタの高さを画面の高さに合わせて動的に計算
  useEffect(() => {
    const calculateEditorHeight = () => {
      const windowHeight = window.innerHeight;
      const editorContainer = editorContainerRef.current?.closest('.post-create-content');
      if (!editorContainer) {
        return;
      }

      // エディタより上の要素の高さを計算
      const header = editorContainer.querySelector('.post-create-header');
      const tabs = editorContainer.querySelector('.language-tabs');
      
      let topElementsHeight = 0;
      if (header) {
        topElementsHeight += header.getBoundingClientRect().height;
      }
      if (tabs) {
        topElementsHeight += tabs.getBoundingClientRect().height;
      }
      
      // エディタより前のフォーム要素の高さを計算
      const editorFormGroup = editorContainerRef.current?.closest('.form-group');
      if (editorFormGroup) {
        let prevSibling = editorFormGroup.previousElementSibling;
        while (prevSibling) {
          if (prevSibling.classList.contains('form-group')) {
            topElementsHeight += prevSibling.getBoundingClientRect().height;
            topElementsHeight += 20; // gap
          }
          prevSibling = prevSibling.previousElementSibling;
        }
      }
      
      // エディタのラベルとアクションボタンの高さを計算
      const contentHeader = editorContainerRef.current?.closest('.form-group')?.querySelector('.content-header');
      if (contentHeader) {
        topElementsHeight += contentHeader.getBoundingClientRect().height;
        topElementsHeight += 8; // gap
      }
      
      // 読み込み済みファイル情報の高さを計算
      const loadedFileInfo = editorContainerRef.current?.previousElementSibling;
      if (loadedFileInfo && loadedFileInfo.classList.contains('loaded-file-info')) {
        topElementsHeight += loadedFileInfo.getBoundingClientRect().height;
        topElementsHeight += 8; // gap
      }
      
      // エディタのform-groupのパディングやボーダーを考慮
      // エディタのform-groupには、ラベル、アクションボタン、読み込み済みファイル情報、エディタ本体が含まれる
      // エディタ本体の高さを計算する際に、form-groupのパディングやボーダーも考慮する必要がある
      // 注意: form-groupのパディングとボーダーは既にtopElementsHeightに含まれている可能性があるため、
      // エディタ本体の高さ計算時には考慮しない
      
      // エディタより下の要素の高さを計算
      let bottomElementsHeight = 0;
      const bottomElements: Array<{ element: Element; height: number }> = [];
      let lastBottomElementBottom = 0;
      if (editorFormGroup) {
        let nextSibling = editorFormGroup.nextElementSibling;
        while (nextSibling) {
          if (nextSibling.classList.contains('form-group')) {
            const rect = nextSibling.getBoundingClientRect();
            const height = rect.height;
            bottomElements.push({ element: nextSibling, height });
            bottomElementsHeight += height;
            bottomElementsHeight += 20; // gap
            lastBottomElementBottom = Math.max(lastBottomElementBottom, rect.bottom);
          }
          nextSibling = nextSibling.nextElementSibling;
        }
      }
      
      // エディタより下の要素の高さを計算（最小限のスペースを確保）
      const bottomPadding = 40; // 下部のパディング
      
      // エディタのform-groupの高さを考慮（ラベル、アクションボタン、読み込み済みファイル情報を含む）
      // エディタ本体の高さを計算する際に、form-groupの高さからエディタ以外の要素の高さを引く必要がある
      // ただし、topElementsHeightには既にcontentHeaderとloadedFileInfoの高さが含まれているため、
      // エディタのform-group全体の高さとエディタ本体の高さの差を考慮する必要がある
      let editorFormGroupExtraHeight = 0;
      if (editorFormGroup) {
        // エディタのform-group全体の高さを取得
        const editorFormGroupHeight = editorFormGroup.getBoundingClientRect().height;
        // エディタ本体の高さを取得（まだ設定されていない場合は0）
        const editorElement = editorContainerRef.current?.querySelector('.w-md-editor');
        const currentEditorHeight = editorElement?.getBoundingClientRect().height || 0;
        // form-groupの高さとエディタ本体の高さの差を計算
        // この差は、ラベル、アクションボタン、読み込み済みファイル情報、gapなどの高さ
        if (currentEditorHeight > 0) {
          editorFormGroupExtraHeight = editorFormGroupHeight - currentEditorHeight;
        } else {
          // エディタがまだレンダリングされていない場合は、contentHeaderとloadedFileInfoの高さを使用
          const contentHeaderHeight = contentHeader?.getBoundingClientRect().height || 0;
          const loadedFileInfoHeight = loadedFileInfo && loadedFileInfo.classList.contains('loaded-file-info') 
            ? loadedFileInfo.getBoundingClientRect().height 
            : 0;
          editorFormGroupExtraHeight = contentHeaderHeight + loadedFileInfoHeight + 8 + 8; // gap分も含む
        }
      }
      
      // 利用可能な高さを計算（下の要素の高さも考慮）
      // エディタの高さは、画面の高さから上部要素、下部要素、パディングを引いた値に制限
      // ただし、エディタのform-group全体の高さ（エディタ本体 + ラベル + アクションボタン + 読み込み済みファイル情報 + gap）を考慮する必要がある
      // topElementsHeightには既にcontentHeaderとloadedFileInfoの高さが含まれているが、
      // エディタのform-group全体の高さとエディタ本体の高さの差（editorFormGroupExtraHeight）を考慮して、
      // エディタ本体の高さを調整する必要がある
      // エディタ本体の高さ = availableHeight - editorFormGroupExtraHeight
      // ただし、editorFormGroupExtraHeightはエディタがレンダリングされた後に正確に計算されるため、
      // 初回計算時は概算値を使用する
      const availableHeight = windowHeight - topElementsHeight - bottomElementsHeight - bottomPadding;
      
      // エディタ本体の高さを計算（form-groupの余分な高さを考慮）
      // エディタ本体の高さは、availableHeightからeditorFormGroupExtraHeightを引いた値
      // さらに、下の要素が画面内に収まるように、bottomElementsHeight分のスペースを確保する必要がある
      // ただし、availableHeightの計算時に既にbottomElementsHeightが考慮されているため、
      // エディタ本体の高さは、availableHeightからeditorFormGroupExtraHeightを引いた値で十分
      // しかし、実際にはspaceBelowがbottomElementsHeightより大きくなるようにする必要がある
      // そのため、エディタ本体の高さをさらに調整する必要がある
      let editorBodyHeight = availableHeight - editorFormGroupExtraHeight;
      
      // 下の要素が画面内に収まるように、エディタ本体の高さを調整
      // エディタのform-groupの実際の位置を取得して、正確なspaceBelowを計算する
      let actualEditorFormGroupTop = 0;
      let actualEditorFormGroupBottom = 0;
      if (editorFormGroup) {
        const rect = editorFormGroup.getBoundingClientRect();
        actualEditorFormGroupTop = rect.top;
        actualEditorFormGroupBottom = rect.bottom;
      }
      
      // 実際の下の要素に必要なスペースを計算
      // lastBottomElementBottomが0でない場合（下の要素が存在する場合）、
      // エディタの下端から最後の下の要素の下端までの距離を計算
      // ただし、エディタの高さがまだ確定していないため、現在のエディタの下端を使用
      let actualSpaceNeededForBottomElements = 0;
      if (lastBottomElementBottom > 0 && actualEditorFormGroupBottom > 0) {
        // 現在のエディタの下端から最後の下の要素の下端までの距離
        actualSpaceNeededForBottomElements = lastBottomElementBottom - actualEditorFormGroupBottom;
      } else if (lastBottomElementBottom > 0) {
        // エディタのform-groupの下端がまだ確定していない場合、
        // エディタのform-groupの上端 + editorFormGroupExtraHeight + editorBodyHeight を仮定
        const estimatedEditorFormGroupBottom = actualEditorFormGroupTop + editorFormGroupExtraHeight + editorBodyHeight;
        actualSpaceNeededForBottomElements = lastBottomElementBottom - estimatedEditorFormGroupBottom;
      }
      
      // spaceBelow = windowHeight - (actualEditorFormGroupTop + editorFormGroupExtraHeight + editorBodyHeight)
      // spaceBelow >= actualSpaceNeededForBottomElements + bottomPadding になるようにする（bottomPadding分の余裕を確保）
      // つまり、editorBodyHeight <= windowHeight - actualEditorFormGroupTop - editorFormGroupExtraHeight - actualSpaceNeededForBottomElements - bottomPadding
      // ただし、actualSpaceNeededForBottomElementsが0以下の場合は、bottomElementsHeightを使用
      const spaceNeededForBottomElements = actualSpaceNeededForBottomElements > 0 ? actualSpaceNeededForBottomElements : bottomElementsHeight;
      const maxEditorBodyHeight = windowHeight - actualEditorFormGroupTop - editorFormGroupExtraHeight - spaceNeededForBottomElements - bottomPadding;
      if (editorBodyHeight > maxEditorBodyHeight) {
        editorBodyHeight = maxEditorBodyHeight;
      }
      
      // 最小高さは500px、最大高さはエディタ本体の高さ（下の要素が画面内に収まるように制限）
      const minHeight = 500;
      // editorBodyHeightが負の値になる場合は、最小高さを使用
      const calculatedHeight = editorBodyHeight > minHeight ? editorBodyHeight : minHeight;
      
      setEditorHeight(calculatedHeight);
    };

    // 初回計算
    calculateEditorHeight();

    // リサイズイベントに対応
    window.addEventListener('resize', calculateEditorHeight);
    
    // コンテンツの変更にも対応（MutationObserver）
    const observer = new MutationObserver(calculateEditorHeight);
    const editorContainer = editorContainerRef.current?.closest('.post-create-content');
    if (editorContainer) {
      observer.observe(editorContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    return () => {
      window.removeEventListener('resize', calculateEditorHeight);
      observer.disconnect();
    };
  }, [content, contentEn, activeTab]);

  return (
    <div className="post-create-container">
      <AdminSidebar />
      <div className="post-create-main">
        <div className="post-create-content">
          <div className="post-create-header">
            <h1>{isEditMode ? '記事編集' : '新規記事作成'}</h1>
            <div className="header-actions">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting || isLoading}
                className="btn-cancel"
              >
                キャンセル
              </button>
              <button
                type="submit"
                form="post-create-form"
                disabled={isSubmitting || isLoading}
                className="btn-submit"
              >
                {isSubmitting ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
          
          {error && (
            <div className="post-create-error">
              {error}
            </div>
          )}

          {isLoading && (
            <div className="post-create-loading">
              読み込み中...
            </div>
          )}

          {/* タブUI */}
          <div className="language-tabs">
            <button
              type="button"
              className={`tab-button ${activeTab === 'ja' ? 'active' : ''}`}
              onClick={() => setActiveTab('ja')}
              disabled={isSubmitting || isLoading}
            >
              日本語
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === 'en' ? 'active' : ''}`}
              onClick={() => setActiveTab('en')}
              disabled={isSubmitting || isLoading}
            >
              English
            </button>
          </div>

          <form id="post-create-form" onSubmit={handleSubmit} className="post-create-form">
            {/* Slug（共通項目）を一番上に配置 */}
            <div className="form-group">
              <label htmlFor="slug">
                {LABELS.SLUG} <span className="required">*</span>
              </label>
              <input
                type="text"
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder={PLACEHOLDERS.SLUG}
                disabled={isSubmitting || isLoading}
                required
              />
              <small>
                {isEditMode 
                  ? MESSAGES.SLUG_DESCRIPTION_EDIT
                  : MESSAGES.SLUG_DESCRIPTION_NEW}
              </small>
            </div>

            {/* 日本語版フォーム */}
            {activeTab === 'ja' && (
              <>
                <div className="form-group">
                  <div className="form-group-header">
                    <label htmlFor="title">
                      {LABELS.TITLE} <span className="required">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleGenerateWithHaiku}
                      disabled={isSubmitting || isLoading || isGenerating || !content.trim()}
                      className="btn-generate-haiku"
                      title="本文からタイトル、概要、Slugを自動生成（Haiku）"
                    >
                      {isGenerating ? BUTTONS.GENERATING : BUTTONS.GENERATE_HAIKU}
                    </button>
                  </div>
                  <input
                    type="text"
                    id="title"
                    value={title}
                    onChange={handleTitleChange}
                    placeholder={PLACEHOLDERS.TITLE}
                    disabled={isSubmitting || isLoading || isGenerating}
                    required
                  />
                </div>

            <div className="form-group">
              <div className="content-header">
                <label htmlFor="content">
                  {LABELS.CONTENT} <span className="required">*</span>
                </label>
                <div className="content-actions">
                  <button
                    type="button"
                    onClick={handleOpenFile}
                    disabled={isSubmitting || isLoading}
                    className="btn-file-open"
                    title="File System Access APIでファイルを開く（Chrome/Edgeのみ）"
                  >
                    {BUTTONS.OPEN_FILE}
                  </button>
                  {fileHandle && (
                    <button
                      type="button"
                      onClick={handleReloadFile}
                      disabled={isSubmitting || isLoading}
                      className="btn-file-reload"
                      title="ファイルを再読み込み"
                    >
                      {BUTTONS.RELOAD_FILE}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleFileDownload}
                    disabled={isSubmitting || isLoading || !content.trim()}
                    className="btn-file-download"
                  >
                    {BUTTONS.DOWNLOAD_FILE}
                  </button>
                  <button
                    type="button"
                    onClick={handleInsertImage}
                    disabled={isSubmitting || isLoading || isUploadingImage}
                    className="btn-insert-image"
                    title="画像ファイルを選択"
                  >
                    {BUTTONS.SELECT_IMAGE}
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenMediaLibrary}
                    disabled={isSubmitting || isLoading}
                    className="btn-select-media"
                    title="アップロード済み画像を選択"
                  >
                    {BUTTONS.SELECT_UPLOADED_IMAGE}
                  </button>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
              {loadedFileName && (
                <div className="loaded-file-info">
                  {MESSAGES.LOADED_FILE}: {loadedFileName}
                </div>
              )}
              <div ref={editorContainerRef} data-color-mode="light">
                <MDEditor
                  value={content}
                  onChange={(value) => setContent(value || '')}
                  preview="live"
                  hideToolbar={false}
                  height={editorHeight}
                  previewOptions={{
                    remarkPlugins: [remarkBreaks],
                    components: {
                      p({ children, ...props }: any) {
                        // 子要素に`post:`形式のリンクが含まれているかチェック
                        // ArticleLinkCardが含まれる場合は`<div>`を返す（`<p>`の中に`<div>`を入れることはできないため）
                        const hasPostIdLink = (() => {
                          if (props.node?.children) {
                            return props.node.children.some((child: any) => {
                              if (child.type === 'element' && child.tagName === 'a') {
                                const href = child.properties?.href || '';
                                // `post:`で始まる場合はtrue
                                if (typeof href === 'string' && href.startsWith('post:')) {
                                  return true;
                                }
                                // hrefが空文字列の場合、元のMarkdownテキストから抽出を試みる
                                if (href === '' && child.position && content) {
                                  const lines = content.split('\n');
                                  const lineIndex = child.position.start.line - 1;
                                  if (lineIndex >= 0 && lineIndex < lines.length) {
                                    const line = lines[lineIndex];
                                    const match = line.match(/\[([^\]]*)\]\(post:([^\)]+)\)/);
                                    if (match) {
                                      return true;
                                    }
                                  }
                                }
                              }
                              return false;
                            });
                          }
                          return false;
                        })();
                        
                        if (hasPostIdLink) {
                          // `post:`形式のリンクが含まれている場合は`<div>`を返す
                          return <div {...props}>{children}</div>;
                        }
                        
                        // 通常の`<p>`タグを返す
                        return <p {...props}>{children}</p>;
                      },
                      a({ href, children, ...props }: any) {
                        // hrefが空文字列の場合、props.node.properties.hrefを確認
                        // ReactMarkdownが`post:`を無効なURLとして扱い、空文字列に変換する可能性がある
                        let actualHref = href;
                        if ((!actualHref || (typeof actualHref === 'string' && actualHref.trim() === '')) && props.node?.properties?.href) {
                          actualHref = props.node.properties.href;
                        }
                        
                        // hrefが空文字列の場合、元のMarkdownテキストから抽出を試みる
                        if ((!actualHref || (typeof actualHref === 'string' && actualHref.trim() === '')) && props.node?.position && content) {
                          const lines = content.split('\n');
                          const lineIndex = props.node.position.start.line - 1;
                          if (lineIndex >= 0 && lineIndex < lines.length) {
                            const line = lines[lineIndex];
                            // `[](post:post_id)`形式を抽出
                            const match = line.match(/\[([^\]]*)\]\(post:([^\)]+)\)/);
                            if (match) {
                              actualHref = `post:${match[2]}`;
                            }
                          }
                        }
                        
                        // hrefがない場合は通常のリンクを返す
                        if (!actualHref || typeof actualHref !== 'string') {
                          return (
                            <a href={actualHref} {...props}>
                              {children}
                            </a>
                          );
                        }

                        // PostID形式のリンクをチェック（`post:post_id`形式）
                        const isPostIdLink = typeof actualHref === 'string' && actualHref.startsWith('post:');
                        if (isPostIdLink) {
                          const postId = actualHref.substring(5); // 'post:'を除く
                          // リンクテキストが空（`[]`）の場合のみPostIDベースのカードを表示
                          const linkText = extractTextFromChildren(children);
                          if (!linkText || linkText.trim() === '') {
                            // 管理画面は日本語のみを想定
                            return <ArticleLinkCardWrapperByPostId postId={postId} lang="ja" />;
                          }
                        }
                        
                        // actualHrefをhrefとして使用
                        href = actualHref;

                        // 外部URLまたはslug抽出に失敗した場合は通常のリンクを返す
                        return (
                          <a href={href} {...props} target="_blank" rel="noopener noreferrer">
                            {children}
                          </a>
                        );
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="excerpt">{LABELS.EXCERPT}</label>
              <textarea
                id="excerpt"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder={PLACEHOLDERS.EXCERPT}
                rows={3}
                disabled={isSubmitting || isLoading}
              />
              <small>{MESSAGES.EXCERPT_DESCRIPTION}</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">
                  {LABELS.CATEGORY} <span className="required">*</span>
                </label>
                <select
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={isSubmitting || isLoading || isLoadingCategories}
                  required
                >
                  <option value="">選択してください</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="status">{LABELS.STATUS}</label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'draft' | 'published')}
                  disabled={isSubmitting || isLoading}
                >
                  <option value="draft">{LABELS.DRAFT}</option>
                  <option value="published">{LABELS.PUBLISHED}</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="tags">{LABELS.TAGS}</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  id="tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder={PLACEHOLDERS.TAGS}
                  disabled={isSubmitting || isLoading}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={handleOpenTagModal}
                  disabled={isSubmitting || isLoading || isLoadingTags}
                  className="btn btn-secondary"
                >
                  既存タグから選択
                </button>
              </div>
              <small>{MESSAGES.TAGS_DESCRIPTION}</small>
            </div>

            <div className="form-group">
              <label htmlFor="thumbnailUrl">{LABELS.THUMBNAIL_URL}</label>
              <div className="thumbnail-url-input-group">
              <input
                type="url"
                id="thumbnailUrl"
                value={thumbnailUrl}
                onChange={(e) => setThumbnailUrl(e.target.value)}
                placeholder={PLACEHOLDERS.THUMBNAIL_URL}
                  disabled={isSubmitting || isLoading || isUploadingThumbnail}
                  className="thumbnail-url-input"
              />
                <button
                  type="button"
                  onClick={handleThumbnailFileSelect}
                  disabled={isSubmitting || isLoading || isUploadingThumbnail}
                  className="btn-thumbnail-select"
                  title="画像ファイルを選択"
                >
                  {BUTTONS.SELECT_FILE}
                </button>
                <button
                  type="button"
                  onClick={handleOpenMediaLibraryForThumbnail}
                  disabled={isSubmitting || isLoading || isUploadingThumbnail}
                  className="btn-thumbnail-select-media"
                  title="アップロード済み画像を選択"
                >
                  {BUTTONS.SELECT_UPLOADED_IMAGE_FOR_THUMBNAIL}
                </button>
                <input
                  ref={thumbnailInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailFileChange}
                  style={{ display: 'none' }}
                />
              </div>
              {(thumbnailUrl || (uploadModalType === 'thumbnail' && uploadModalPreview)) && (
                <div className="thumbnail-preview-section">
                  {uploadModalType === 'thumbnail' && uploadModalPreview ? (
                    <div className="thumbnail-preview">
                      <img
                        src={uploadModalPreview}
                        alt="選択中のサムネイル"
                        className="thumbnail-preview-image"
                        onError={() => {
                          setThumbnailPreviewError(true);
                        }}
                      />
                      <div className="thumbnail-preview-label">{MESSAGES.SELECTED_IMAGE}</div>
                    </div>
                  ) : thumbnailUrl && !thumbnailPreviewError ? (
                    <div className="thumbnail-preview">
                      <img
                        src={thumbnailUrl}
                        alt="サムネイル"
                        className="thumbnail-preview-image"
                        onError={() => {
                          setThumbnailPreviewError(true);
                        }}
                        onLoad={() => {
                          setThumbnailPreviewError(false);
                        }}
                      />
                      <div className="thumbnail-preview-label">{MESSAGES.CURRENT_THUMBNAIL}</div>
                    </div>
                  ) : thumbnailPreviewError ? (
                    <div className="thumbnail-preview-error">
                      {MESSAGES.FAILED_TO_LOAD_IMAGE}
                    </div>
                  ) : null}
                </div>
              )}
              <small>{MESSAGES.THUMBNAIL_DESCRIPTION}</small>
            </div>
              </>
            )}

            {/* 英語版フォーム */}
            {activeTab === 'en' && (
              <>
                <div className="form-group">
                  <div className="form-group-header">
                    <label htmlFor="titleEn">
                      {LABELS.TITLE} <span className="required">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleGenerateWithHaikuEn}
                      disabled={isSubmitting || isLoading || isGeneratingEn || !content.trim()}
                      className="btn-generate-haiku"
                      title="本文からタイトル、概要、Slugを自動生成（Haiku）"
                    >
                      {isGeneratingEn ? BUTTONS.GENERATING : BUTTONS.GENERATE_HAIKU}
                    </button>
                  </div>
                  <input
                    type="text"
                    id="titleEn"
                    value={titleEn}
                    onChange={handleTitleChangeEn}
                    placeholder={PLACEHOLDERS.TITLE}
                    disabled={isSubmitting || isLoading || isGeneratingEn}
                    required
                  />
                </div>

                <div className="form-group">
                  <div className="content-header">
                    <label htmlFor="contentEn">
                      {LABELS.CONTENT} <span className="required">*</span>
                    </label>
                    <div className="content-actions">
                      <button
                        type="button"
                        onClick={handleOpenFileEn}
                        disabled={isSubmitting || isLoading}
                        className="btn-file-open"
                        title="File System Access APIでファイルを開く（Chrome/Edgeのみ）"
                      >
                        {BUTTONS.OPEN_FILE}
                      </button>
                      {fileHandleEn && (
                        <button
                          type="button"
                          onClick={handleReloadFileEn}
                          disabled={isSubmitting || isLoading}
                          className="btn-file-reload"
                          title="ファイルを再読み込み"
                        >
                          {BUTTONS.RELOAD_FILE}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleFileDownloadEn}
                        disabled={isSubmitting || isLoading || !contentEn.trim()}
                        className="btn-file-download"
                      >
                        {BUTTONS.DOWNLOAD_FILE}
                      </button>
                      <button
                        type="button"
                        onClick={handleInsertImageEn}
                        disabled={isSubmitting || isLoading || isUploadingImage}
                        className="btn-insert-image"
                        title="画像ファイルを選択"
                      >
                        {BUTTONS.SELECT_IMAGE}
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenMediaLibrary}
                        disabled={isSubmitting || isLoading}
                        className="btn-select-media"
                        title="アップロード済み画像を選択"
                      >
                        {BUTTONS.SELECT_UPLOADED_IMAGE}
                      </button>
                      <input
                        ref={imageInputRefEn}
                        type="file"
                        accept="image/*"
                        onChange={handleImageFileChangeEn}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>
                  {loadedFileNameEn && (
                    <div className="loaded-file-info">
                      {MESSAGES.LOADED_FILE}: {loadedFileNameEn}
                    </div>
                  )}
                  <div ref={editorContainerRefEn} data-color-mode="light">
                    <MDEditor
                      value={contentEn}
                      onChange={(value) => setContentEn(value || '')}
                      preview="live"
                      hideToolbar={false}
                      height={editorHeight}
                      previewOptions={{
                        remarkPlugins: [remarkBreaks],
                        components: {
                          p({ children, ...props }: any) {
                            // 子要素に`post:`形式のリンクが含まれているかチェック
                            // ArticleLinkCardが含まれる場合は`<div>`を返す（`<p>`の中に`<div>`を入れることはできないため）
                            const hasPostIdLink = (() => {
                              if (props.node?.children) {
                                return props.node.children.some((child: any) => {
                                  if (child.type === 'element' && child.tagName === 'a') {
                                    const href = child.properties?.href || '';
                                    // `post:`で始まる場合はtrue
                                    if (typeof href === 'string' && href.startsWith('post:')) {
                                      return true;
                                    }
                                    // hrefが空文字列の場合、元のMarkdownテキストから抽出を試みる
                                    if (href === '' && child.position && contentEn) {
                                      const lines = contentEn.split('\n');
                                      const lineIndex = child.position.start.line - 1;
                                      if (lineIndex >= 0 && lineIndex < lines.length) {
                                        const line = lines[lineIndex];
                                        const match = line.match(/\[([^\]]*)\]\(post:([^\)]+)\)/);
                                        if (match) {
                                          return true;
                                        }
                                      }
                                    }
                                  }
                                  return false;
                                });
                              }
                              return false;
                            })();
                            
                            if (hasPostIdLink) {
                              // `post:`形式のリンクが含まれている場合は`<div>`を返す
                              return <div {...props}>{children}</div>;
                            }
                            
                            // 通常の`<p>`タグを返す
                            return <p {...props}>{children}</p>;
                          },
                          a({ href, children, ...props }: any) {
                            // hrefが空文字列の場合、props.node.properties.hrefを確認
                            // ReactMarkdownが`post:`を無効なURLとして扱い、空文字列に変換する可能性がある
                            let actualHref = href;
                            if ((!actualHref || (typeof actualHref === 'string' && actualHref.trim() === '')) && props.node?.properties?.href) {
                              actualHref = props.node.properties.href;
                            }
                            
                            // hrefが空文字列の場合、元のMarkdownテキストから抽出を試みる
                            if ((!actualHref || (typeof actualHref === 'string' && actualHref.trim() === '')) && props.node?.position && contentEn) {
                              const lines = contentEn.split('\n');
                              const lineIndex = props.node.position.start.line - 1;
                              if (lineIndex >= 0 && lineIndex < lines.length) {
                                const line = lines[lineIndex];
                                // `[](post:post_id)`形式を抽出
                                const match = line.match(/\[([^\]]*)\]\(post:([^\)]+)\)/);
                                if (match) {
                                  actualHref = `post:${match[2]}`;
                                }
                              }
                            }
                            
                            // hrefがない場合は通常のリンクを返す
                            if (!actualHref || typeof actualHref !== 'string') {
                              return (
                                <a href={actualHref} {...props}>
                                  {children}
                                </a>
                              );
                            }

                            // PostID形式のリンクをチェック（`post:post_id`形式）
                            const isPostIdLink = typeof actualHref === 'string' && actualHref.startsWith('post:');
                            if (isPostIdLink) {
                              const postId = actualHref.substring(5); // 'post:'を除く
                              // リンクテキストが空（`[]`）の場合のみPostIDベースのカードを表示
                              const linkText = extractTextFromChildren(children);
                              if (!linkText || linkText.trim() === '') {
                                // 英語版の記事リンクカードを表示
                                return <ArticleLinkCardWrapperByPostId postId={postId} lang="en" />;
                              }
                            }
                            
                            // actualHrefをhrefとして使用
                            href = actualHref;

                            // 外部URLまたはslug抽出に失敗した場合は通常のリンクを返す
                            return (
                              <a href={href} {...props} target="_blank" rel="noopener noreferrer">
                                {children}
                              </a>
                            );
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="excerptEn">{LABELS.EXCERPT}</label>
                  <textarea
                    id="excerptEn"
                    value={excerptEn}
                    onChange={(e) => setExcerptEn(e.target.value)}
                    placeholder={PLACEHOLDERS.EXCERPT}
                    rows={3}
                    disabled={isSubmitting || isLoading || isGeneratingEn}
                  />
                  <small>{MESSAGES.EXCERPT_DESCRIPTION}</small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="category">
                      {LABELS.CATEGORY} <span className="required">*</span>
                    </label>
                    <select
                      id="category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      disabled={isSubmitting || isLoading || isLoadingCategories}
                      required
                    >
                      <option value="">選択してください</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="status">{LABELS.STATUS}</label>
                    <select
                      id="status"
                      value={status}
                      onChange={(e) => setStatus(e.target.value as 'draft' | 'published')}
                      disabled={isSubmitting || isLoading}
                    >
                      <option value="draft">{LABELS.DRAFT}</option>
                      <option value="published">{LABELS.PUBLISHED}</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="tagsEn">{LABELS.TAGS}</label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      id="tagsEn"
                      value={tagsEn}
                      onChange={(e) => setTagsEn(e.target.value)}
                      placeholder={PLACEHOLDERS.TAGS}
                      disabled={isSubmitting || isLoading}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={handleOpenTagModal}
                      disabled={isSubmitting || isLoading || isLoadingTags}
                      className="btn btn-secondary"
                    >
                      既存タグから選択
                    </button>
                  </div>
                  <small>{MESSAGES.TAGS_DESCRIPTION}</small>
                </div>

                <div className="form-group">
                  <label htmlFor="thumbnailUrl">{LABELS.THUMBNAIL_URL}</label>
                  <div className="thumbnail-url-input-group">
                    <input
                      type="url"
                      id="thumbnailUrl"
                      value={thumbnailUrl}
                      onChange={(e) => setThumbnailUrl(e.target.value)}
                      placeholder={PLACEHOLDERS.THUMBNAIL_URL}
                      disabled={isSubmitting || isLoading || isUploadingThumbnail}
                      className="thumbnail-url-input"
                    />
                    <button
                      type="button"
                      onClick={handleThumbnailFileSelect}
                      disabled={isSubmitting || isLoading || isUploadingThumbnail}
                      className="btn-thumbnail-select"
                      title="画像ファイルを選択"
                    >
                      {BUTTONS.SELECT_FILE}
                    </button>
                    <button
                      type="button"
                      onClick={handleOpenMediaLibraryForThumbnail}
                      disabled={isSubmitting || isLoading || isUploadingThumbnail}
                      className="btn-thumbnail-select-media"
                      title="アップロード済み画像を選択"
                    >
                      {BUTTONS.SELECT_UPLOADED_IMAGE_FOR_THUMBNAIL}
                    </button>
                    <input
                      ref={thumbnailInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailFileChange}
                      style={{ display: 'none' }}
                    />
                  </div>
                  {(thumbnailUrl || (uploadModalType === 'thumbnail' && uploadModalPreview)) && (
                    <div className="thumbnail-preview-section">
                      {uploadModalType === 'thumbnail' && uploadModalPreview ? (
                        <div className="thumbnail-preview">
                          <img
                            src={uploadModalPreview}
                            alt="選択中のサムネイル"
                            className="thumbnail-preview-image"
                            onError={() => {
                              setThumbnailPreviewError(true);
                            }}
                          />
                          <div className="thumbnail-preview-label">{MESSAGES.SELECTED_IMAGE}</div>
                        </div>
                      ) : thumbnailUrl && !thumbnailPreviewError ? (
                        <div className="thumbnail-preview">
                          <img
                            src={thumbnailUrl}
                            alt="サムネイル"
                            className="thumbnail-preview-image"
                            onError={() => {
                              setThumbnailPreviewError(true);
                            }}
                            onLoad={() => {
                              setThumbnailPreviewError(false);
                            }}
                          />
                          <div className="thumbnail-preview-label">{MESSAGES.CURRENT_THUMBNAIL}</div>
                        </div>
                      ) : thumbnailPreviewError ? (
                        <div className="thumbnail-preview-error">
                          {MESSAGES.FAILED_TO_LOAD_IMAGE}
                        </div>
                      ) : null}
                    </div>
                  )}
                  <small>{MESSAGES.THUMBNAIL_DESCRIPTION}</small>
                </div>
              </>
            )}
          </form>
        </div>
      </div>

      {/* 画像アップロードモーダル */}
      {isUploadModalOpen && uploadModalFile && (
        <div
          className="upload-modal"
          role="dialog"
          aria-modal="true"
          onClick={handleCloseUploadModal}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              handleCloseUploadModal();
            }
          }}
          tabIndex={-1}
        >
          <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="upload-modal-close"
              onClick={handleCloseUploadModal}
              aria-label="閉じる"
              disabled={isUploadingThumbnail || isUploadingImage}
            >
              ×
            </button>
            <h2 className="upload-modal-title">
              {uploadModalType === 'thumbnail' ? 'サムネイル画像をアップロード' : '画像をアップロード'}
            </h2>
            <div className="upload-modal-body">
              {error && (
                <div className="upload-modal-error">
                  {error}
                </div>
              )}
              <div className="upload-modal-preview">
                {uploadModalPreview ? (
                  <img
                    src={uploadModalPreview}
                    alt="プレビュー"
                    className="preview-image"
                    onError={(e) => {
                      console.error('画像の読み込みに失敗しました:', e);
                      setPreviewError(true);
                      setUploadModalPreview(null);
                    }}
                    onLoad={() => {
                      setPreviewError(false);
                    }}
                  />
                ) : previewError ? (
                  <div className="preview-error-message">
                    <p>この画像形式はプレビュー表示に対応していません</p>
                    <p className="preview-error-hint">
                      （RAW形式など、一部の画像形式はブラウザで直接表示できません）
                    </p>
                    <p className="preview-error-hint">
                      アップロードは可能です
                    </p>
                  </div>
                ) : (
                  <div className="preview-placeholder">
                    <span>プレビュー</span>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="upload-modal-filename">ファイル名</label>
                <input
                  id="upload-modal-filename"
                  type="text"
                  value={uploadModalFilename}
                  onChange={(e) => {
                    setUploadModalFilename(e.target.value);
                    // ファイル名を変更したらエラーをクリア
                    if (error) {
                      setError(null);
                    }
                  }}
                  disabled={isUploadingThumbnail || isUploadingImage}
                  className="filename-input"
                  placeholder="ファイル名を入力"
                />
                <small className="filename-hint">
                  拡張子は自動的に保持されます。元のファイル名: {uploadModalFile.name}
                </small>
              </div>
              <div className="upload-modal-file-info">
                <span className="file-info-label">選択中のファイル:</span>
                <span className="file-info-name">{uploadModalFile.name}</span>
                <span className="file-info-size">
                  ({(uploadModalFile.size / 1024).toFixed(2)} KB)
                </span>
                <button
                  type="button"
                  onClick={handleReselectFile}
                  disabled={isUploadingThumbnail || isUploadingImage}
                  className="btn-reselect"
                  title="別のファイルを選択"
                >
                  再選択
                </button>
              </div>
              <div className="upload-modal-actions">
                <button
                  type="button"
                  onClick={handleCloseUploadModal}
                  disabled={isUploadingThumbnail || isUploadingImage}
                  className="btn-cancel"
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={isSubmitting || isLoading || isUploadingThumbnail || isUploadingImage}
                  className="btn-upload"
                >
                  {isUploadingThumbnail || isUploadingImage
                    ? 'アップロード中...'
                    : uploadModalType === 'thumbnail'
                    ? 'アップロード'
                    : 'アップロードして挿入'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* メディアライブラリモーダル */}
      {isMediaLibraryOpen && (
        <div
          className="media-library-modal"
          role="dialog"
          aria-modal="true"
          onClick={handleCloseMediaLibrary}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              handleCloseMediaLibrary();
            }
          }}
          tabIndex={-1}
        >
          <div className="media-library-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="media-library-modal-close"
              onClick={handleCloseMediaLibrary}
              aria-label="閉じる"
            >
              ×
            </button>
            <h2 className="media-library-modal-title">
              {mediaLibraryMode === 'thumbnail' ? 'サムネイル画像を選択' : 'アップロード済み画像を選択'}
            </h2>
            <div className="media-library-modal-body">
              {mediaError && (
                <div className="media-library-error">
                  {mediaError}
                </div>
              )}
              {isLoadingMedia && mediaList.length === 0 ? (
                <div className="media-library-loading">読み込み中...</div>
              ) : mediaList.length === 0 ? (
                <div className="media-library-empty">アップロード済みの画像がありません</div>
              ) : (
                <>
                  <div className="media-library-grid">
                    {mediaList
                      .filter((media) => media.cloudfront_url) // CloudFront URLがある画像のみ表示
                      .map((media) => {
                        const imageUrl = media.cloudfront_url!;
                        return (
                          <div
                            key={media.media_id}
                            className="media-library-item"
                            onClick={() => handleSelectMedia(media)}
                            title={media.original_filename}
                          >
                            <div className="media-library-item-image">
                              <img
                                src={imageUrl}
                                alt={media.original_filename}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                            <div className="media-library-item-info">
                              <div className="media-library-item-name" title={media.original_filename}>
                                {media.original_filename}
                              </div>
                              <div className="media-library-item-size">
                                {(media.file_size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  {mediaList.filter((media) => !media.cloudfront_url).length > 0 && (
                    <div className="media-library-warning">
                      {mediaList.filter((media) => !media.cloudfront_url).length}件の画像はCloudFront URLが設定されていないため表示されていません。
                    </div>
                  )}
                  {mediaHasNext && (
                    <div className="media-library-load-more">
                      <button
                        type="button"
                        onClick={handleLoadMoreMedia}
                        disabled={isLoadingMedia}
                        className="btn-load-more"
                      >
                        {isLoadingMedia ? '読み込み中...' : 'さらに読み込む'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* タグ選択モーダル */}
      {isTagModalOpen && (
        <div
          className="media-library-modal"
          role="dialog"
          aria-modal="true"
          onClick={handleCloseTagModal}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              handleCloseTagModal();
            }
          }}
          tabIndex={-1}
        >
          <div className="media-library-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="media-library-header">
              <h2>タグを選択</h2>
              <button
                type="button"
                className="media-library-close"
                onClick={handleCloseTagModal}
                aria-label="閉じる"
              >
                ×
              </button>
            </div>
            <div className="media-library-body">
              {isLoadingTags ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>タグを読み込み中...</div>
              ) : (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <input
                      type="text"
                      placeholder="タグを検索..."
                      value={tagSearchQuery}
                      onChange={(e) => setTagSearchQuery(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      padding: '8px',
                    }}
                  >
                    {existingTags
                      .filter((tag) => tag.isVisible)
                      .filter((tag) => {
                        if (!tagSearchQuery.trim()) return true;
                        const query = tagSearchQuery.toLowerCase();
                        return (
                          tag.name.toLowerCase().includes(query) ||
                          (tag.nameEn && tag.nameEn.toLowerCase().includes(query))
                        );
                      })
                      .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
                          .map((tag) => {
                            const isSelected = selectedTagIds.includes(tag.id);
                        return (
                          <label
                            key={tag.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              padding: '8px 12px',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              backgroundColor: isSelected ? '#e3f2fd' : '#fff',
                              minWidth: '120px',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleTagToggle(tag)}
                              disabled={isSubmitting || isLoading}
                              style={{ marginRight: '8px' }}
                            />
                            <div>
                              <div style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>{tag.name}</div>
                              {tag.nameEn && (
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                  {tag.nameEn}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                  </div>
                  {existingTags.filter((tag) => tag.isVisible).length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                      表示可能なタグがありません
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="media-library-footer">
              <button
                type="button"
                onClick={handleCloseTagModal}
                className="btn btn-primary"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PostCreate;






